<template>
  <div class="video-owner-layout" data-testid="video-owner-layout">
    <VideoOwnerTopBar />

    <!-- Video + Smart Actions Row -->
    <div class="video-owner-layout__media-row">
      <!-- Video Player + Metadata -->
      <div class="video-owner-layout__video-col">
        <div class="video-owner-layout__video">
          <ViewerContent />
        </div>
        <MetadataSection />
      </div>

      <!-- Smart Actions Side Panel -->
      <div class="video-owner-layout__side-panel">
        <SmartActionsSidePanel v-if="hasAiAddon" />
        <div v-else class="video-owner-layout__upsell">
          <div class="upsell-content">
            <Icon name="general.stars" size="lg" class="upsell-icon" />
            <h3 class="upsell-title">Unlock Smart Actions</h3>
            <p class="upsell-description">Transform your videos into instant insights with AI-powered summaries, action items, and more.</p>
            <Button type="solid" variant="blue" text="Learn more" href="/account/plans" data-testid="smart-actions-upsell-btn" />
          </div>
        </div>
      </div>
    </div>

    <!-- Content Tabs -->
    <div class="video-owner-layout__tabs">
      <VideoOwnerTabs @addedComment="emit('addedComment')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon, Button } from '@/storybook';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import SmartActionsSidePanel from '@/modules/viewer/components/SmartActionsSidePanel.vue';
import VideoOwnerTopBar from './VideoOwnerTopBar.vue';
import VideoOwnerTabs from './VideoOwnerTabs.vue';
import MetadataSection from '../../shared/MetadataSection.vue';

import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';

const emit = defineEmits<{
  addedComment: [];
}>();

const { hasAiAddon } = useVideoAi();
</script>

<style scoped>
.video-owner-layout {
  @apply flex flex-col w-full;
}

.video-owner-layout__media-row {
  @apply flex flex-col gap-4;
}

@media (min-width: 1024px) {
  .video-owner-layout__media-row {
    display: grid;
    grid-template-columns: 1fr minmax(280px, 360px);
    align-items: stretch;
    gap: 1rem;
  }
}

@media (min-width: 1440px) {
  .video-owner-layout__media-row {
    grid-template-columns: 1fr 360px;
  }
}

.video-owner-layout__video-col {
  @apply flex flex-col min-w-0;
}

.video-owner-layout__video {
  @apply min-w-0;
}

.video-owner-layout__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

/* Mobile: side panel below video, collapsible */
.video-owner-layout__side-panel {
  @apply flex flex-col w-full mt-2;
}

/* Desktop: side column */
@media (min-width: 1024px) {
  .video-owner-layout__side-panel {
    @apply mt-0;
  }

  .video-owner-layout__side-panel :deep(.smart-actions-panel) {
    @apply flex-grow;
    max-height: 100%;
    overflow-y: auto;
  }
}

.video-owner-layout__upsell {
  @apply bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6 h-full flex items-center;
}

.upsell-content {
  @apply flex flex-col items-center text-center gap-2 sm:gap-3;
}

.upsell-title {
  @apply text-base sm:text-lg font-semibold text-zinc-900 dark:text-zinc-100;
}

.upsell-description {
  @apply text-xs sm:text-sm text-zinc-600 dark:text-zinc-400;
}

.upsell-icon {
  color: #9557af;
}

.dark .upsell-icon {
  color: #e0adff;
}

.video-owner-layout__tabs {
  @apply mt-2;
}
</style>
